import { contactClubIds } from "../../contacts/contactModel";
import type { Identifier, DataProvider } from "ra-core";

import type { Contact, Task, ContactNote } from "../../types";

/**
 * Merge one contact (loser) into another contact (winner).
 *
 * This function copies properties from the loser to the winner contact,
 * transfers all associated data (tasks, notes) from the loser to the winner,
 * and deletes the loser contact.
 */
export const mergeContacts = async (
  loserId: Identifier,
  winnerId: Identifier,
  dataProvider: DataProvider,
) => {
  // Fetch both contacts using dataProvider to get fresh data
  const { data: winnerContact } = await dataProvider.getOne<Contact>(
    "contacts",
    { id: winnerId },
  );
  const { data: loserContact } = await dataProvider.getOne<Contact>(
    "contacts",
    { id: loserId },
  );

  if (!winnerContact || !loserContact) {
    throw new Error("Could not fetch contacts");
  }
  if (
    (winnerContact.contact_type ?? "referee") !==
    (loserContact.contact_type ?? "referee")
  ) {
    throw new Error("Only contacts of the same type can be merged");
  }

  // 1. Reassign all tasks from loser to winner
  const { data: loserTasks } = await dataProvider.getManyReference<Task>(
    "tasks",
    {
      target: "contact_id",
      id: loserId,
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
  );

  const taskUpdates =
    loserTasks?.map((task) =>
      dataProvider.update("tasks", {
        id: task.id,
        data: { contact_id: winnerId },
        previousData: task,
      }),
    ) || [];

  // 2. Reassign all notes from loser to winner
  const { data: loserNotes } = await dataProvider.getManyReference<ContactNote>(
    "contact_notes",
    {
      target: "contact_id",
      id: loserId,
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
  );

  const noteUpdates =
    loserNotes?.map((note) =>
      dataProvider.update<ContactNote>("contact_notes", {
        id: note.id,
        data: { contact_id: winnerId },
        previousData: note,
      }),
    ) || [];

  // 3. Repoint contacts referred by the loser to the winner. This must
  // happen before the loser is deleted, otherwise the `referred_by_id`
  // foreign key's `ON DELETE SET NULL` silently orphans those references.
  // The winner itself is excluded so it can never end up referred by itself.
  const { data: loserReferrals } = await dataProvider.getManyReference<Contact>(
    "contacts",
    {
      target: "referred_by_id",
      id: loserId,
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
  );

  const referralIds = (loserReferrals || [])
    .filter((contact) => contact.id !== winnerId)
    .map((contact) => contact.id);

  const referralUpdate = dataProvider.updateMany<Contact>("contacts", {
    ids: referralIds,
    data: { referred_by_id: winnerId },
  });

  // 4. Update winner contact with loser data
  const mergedEmails = mergeObjectArraysUnique(
    winnerContact.email_jsonb || [],
    loserContact.email_jsonb || [],
    (email) => email.email,
  );

  const mergedPhones = mergeObjectArraysUnique(
    winnerContact.phone_jsonb || [],
    loserContact.phone_jsonb || [],
    (phone) => phone.number,
  );

  const winnerUpdate = dataProvider.update<Contact>("contacts", {
    id: winnerId,
    data: {
      avatar:
        winnerContact.avatar && winnerContact.avatar.src
          ? winnerContact.avatar
          : loserContact.avatar,
      gender: winnerContact.gender ?? loserContact.gender,
      first_name: winnerContact.first_name ?? loserContact.first_name,
      last_name: winnerContact.last_name ?? loserContact.last_name,
      title: winnerContact.title ?? loserContact.title,
      company_ids:
        winnerContact.contact_type === "partner"
          ? contactClubIds(winnerContact).length
            ? contactClubIds(winnerContact)
            : contactClubIds(loserContact)
          : [
              ...new Set([
                ...contactClubIds(winnerContact),
                ...contactClubIds(loserContact),
              ]),
            ],
      referred_by_id:
        winnerContact.referred_by_id ?? loserContact.referred_by_id,
      email_jsonb: mergedEmails,
      phone_jsonb: mergedPhones,
      linkedin_url: winnerContact.linkedin_url || loserContact.linkedin_url,
      background: winnerContact.background ?? loserContact.background,
      has_newsletter:
        winnerContact.has_newsletter ?? loserContact.has_newsletter,
      first_seen: winnerContact.first_seen ?? loserContact.first_seen,
      last_seen:
        winnerContact.last_seen > loserContact.last_seen
          ? winnerContact.last_seen
          : loserContact.last_seen,
      sales_id: winnerContact.sales_id ?? loserContact.sales_id,
      tags: mergeArraysUnique(
        winnerContact.tags || [],
        loserContact.tags || [],
      ),
      client_checklist: mergeArraysUnique(
        winnerContact.client_checklist || [],
        loserContact.client_checklist || [],
      ),
    },
    previousData: winnerContact,
  });

  // Execute all updates
  await Promise.all([
    ...taskUpdates,
    ...noteUpdates,
    referralUpdate,
    winnerUpdate,
  ]);

  // Preserve club-specific progress when referee records are merged. Links
  // for different clubs remain independent; duplicate club links are united.
  if ((winnerContact.contact_type ?? "referee") === "referee") {
    const { data: sourceLinks = [] } = await dataProvider.getManyReference<any>(
      "club_referees",
      {
        target: "contact_id",
        id: loserId,
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter: {},
      },
    );
    const { data: targetLinks = [] } = await dataProvider.getManyReference<any>(
      "club_referees",
      {
        target: "contact_id",
        id: winnerId,
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter: {},
      },
    );
    for (const source of sourceLinks) {
      const target = targetLinks.find(
        (link: any) => String(link.company_id) === String(source.company_id),
      );
      if (target) {
        await dataProvider.update("club_referees", {
          id: target.id,
          data: {
            client_checklist: mergeArraysUnique(
              target.client_checklist ?? [],
              source.client_checklist ?? [],
            ),
          },
          previousData: target,
        });
        await dataProvider.delete("club_referees", {
          id: source.id,
          previousData: source,
        });
      } else {
        await dataProvider.update("club_referees", {
          id: source.id,
          data: { contact_id: winnerId },
          previousData: source,
        });
      }
    }
  }

  // 5. Delete the loser contact
  await dataProvider.delete<Contact>("contacts", {
    id: loserId,
    previousData: loserContact,
  });
};

// Helper functions to merge arrays and remove duplicates

// For primitive arrays like tags
const mergeArraysUnique = <T>(arr1: T[], arr2: T[]): T[] => [
  ...new Set([...arr1, ...arr2]),
];

// For object arrays like emails and phones
function mergeObjectArraysUnique<T>(
  arr1: T[],
  arr2: T[],
  getKey: (item: T) => string,
): T[] {
  const map = new Map<string, T>();

  arr1.forEach((item) => {
    const key = getKey(item);
    if (key) map.set(key, item);
  });

  arr2.forEach((item) => {
    const key = getKey(item);
    if (key && !map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
}
